import { t } from '@lingui/core/macro'
import { EventData } from 'features/systemLens'
import {
  CHART_COLORS,
  DEFAULT_CHART_SETTINGS,
  UPLOT_TICK_FONT,
} from 'features/timeSeries/config/constants'
import { SentinelCaseMarker } from 'features/timeSeries/hooks/useSentinelCases'
import {
  nearestBucketIndex,
  SCORE_GAP_FACTOR,
  scoreCadenceMs,
  scoreContiguityLimitMs,
  SENTINEL_OUT_OF_BAND_COLOR,
  SentinelBandOverlay,
  SIGMA_WARN,
} from 'features/timeSeries/hooks/useSentinelScores'
import uPlot from 'uplot'

import {
  getEventLineColor,
  getEventLineDash,
  getEventLineWidth,
  getThresholdColor,
  getThresholdLabel,
} from './chartHelpers'

interface ThresholdValue {
  value: number
  type: string
  sensors: string[]
}

interface ThresholdPluginOptions {
  thresholds: ThresholdValue[]
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  enhancedSensorEntries: Array<{ id: string }>
  singleScaleMode: boolean
}

export function thresholdsPlugin(
  options: ThresholdPluginOptions
): uPlot.Plugin {
  const {
    thresholds,
    sensorAxisMapping,
    enhancedSensorEntries,
    singleScaleMode,
  } = options

  return {
    hooks: {
      draw: (u: uPlot) => {
        const ctx = u.ctx

        thresholds.forEach((threshold) => {
          const firstSensor = threshold.sensors[0]
          const sensor = enhancedSensorEntries.find((e) =>
            e.id.includes(firstSensor)
          )
          const sensorId = sensor?.id || firstSensor
          const scaleKey = singleScaleMode
            ? 'y'
            : sensorAxisMapping[sensorId]
              ? sensorId
              : 'y'

          const yPos = u.valToPos(threshold.value, scaleKey, true)

          // Skip if outside the plotting area
          if (yPos < u.bbox.top || yPos > u.bbox.top + u.bbox.height) return

          const color = getThresholdColor(threshold.type)
          const label = `${getThresholdLabel(threshold.type)} (${threshold.sensors.join(', ')})`

          ctx.save()
          ctx.beginPath()
          ctx.setLineDash([6, 4])
          ctx.lineWidth = 2
          ctx.strokeStyle = color
          ctx.moveTo(u.bbox.left, yPos)
          ctx.lineTo(u.bbox.left + u.bbox.width, yPos)
          ctx.stroke()

          ctx.font = `${DEFAULT_CHART_SETTINGS.TICK_FONT_SIZE * devicePixelRatio}px ${DEFAULT_CHART_SETTINGS.FONT_FAMILY}`
          ctx.fillStyle = color
          ctx.textAlign =
            sensorAxisMapping[sensorId]?.side === 'right' ? 'right' : 'left'
          ctx.textBaseline = threshold.type.includes('upper') ? 'bottom' : 'top'

          const xTextPos =
            sensorAxisMapping[sensorId]?.side === 'right'
              ? u.bbox.left + u.bbox.width * 0.9
              : u.bbox.left + u.bbox.width * 0.1
          ctx.fillText(label, xTextPos, yPos)
          ctx.restore()
        })
      },
    },
  }
}

interface EventMarkersPluginOptions {
  events: EventData[]
}

export function eventMarkersPlugin(
  options: EventMarkersPluginOptions
): uPlot.Plugin {
  const { events } = options

  return {
    hooks: {
      draw: (u: uPlot) => {
        if (events.length === 0) return

        const ctx = u.ctx

        events.forEach((event) => {
          const timestamp = new Date(event.timestamp).getTime()
          const xPos = u.valToPos(timestamp, 'x', true)

          // Skip if outside plotting area
          if (xPos < u.bbox.left || xPos > u.bbox.left + u.bbox.width) return

          const lineColor = getEventLineColor(event.metadata?.priority)
          const lineWidth = getEventLineWidth(event.metadata?.priority)
          const lineDash = getEventLineDash(event.metadata?.status)

          ctx.save()
          ctx.beginPath()
          ctx.setLineDash(lineDash)
          ctx.lineWidth = lineWidth * devicePixelRatio
          ctx.strokeStyle = lineColor
          ctx.moveTo(xPos, u.bbox.top)
          ctx.lineTo(xPos, u.bbox.top + u.bbox.height)
          ctx.stroke()

          // Draw annotation label
          const annotationText = event.type
          const fontSize =
            DEFAULT_CHART_SETTINGS.TICK_FONT_SIZE * devicePixelRatio
          ctx.font = `${fontSize}px ${DEFAULT_CHART_SETTINGS.FONT_FAMILY}`

          const textMetrics = ctx.measureText(annotationText)
          const padding = 2 * devicePixelRatio
          const textHeight = fontSize

          const labelX = xPos - textMetrics.width / 2 - padding
          const labelY = u.bbox.top - textHeight - padding * 3

          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
          ctx.strokeStyle = lineColor
          ctx.lineWidth = devicePixelRatio
          ctx.setLineDash([])
          ctx.fillRect(
            labelX,
            labelY,
            textMetrics.width + padding * 2,
            textHeight + padding * 2
          )
          ctx.strokeRect(
            labelX,
            labelY,
            textMetrics.width + padding * 2,
            textHeight + padding * 2
          )

          ctx.fillStyle = lineColor
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'
          ctx.fillText(annotationText, xPos, u.bbox.top - padding)
          ctx.restore()
        })
      },
    },
  }
}

interface SentinelBandsPluginOptions {
  /** Reuses the hook's overlay type so the shapes cannot drift apart. */
  overlays: Array<
    Pick<
      SentinelBandOverlay,
      | 'targetSensorId'
      | 'label'
      | 'color'
      | 'xs'
      | 'upper'
      | 'lower'
      | 'center'
      | 'actual'
      | 'sigma'
      | 'trainedSplitMs'
      | 'modelCreatedMs'
      | 'windowStartMs'
      | 'splitRanges'
      | 'centerPenId'
      | 'warnSigma'
      | 'showSplitRegions'
      | 'showOutOfBandMarks'
      | 'gapToleranceMs'
    >
  >
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  singleScaleMode: boolean
  /**
   * Styling for the center (expected) line. Sentinels keep the default: a
   * faint dotted white trace that reads as texture inside the band. The
   * experiment overlay passes a prominent style so the model's PREDICTION is
   * a first-class line the user can follow against the actual pen.
   */
  centerLine?: {
    color?: string
    alpha?: number
    widthPx?: number
    /** Dash pattern in CSS px, scaled by devicePixelRatio when drawn. */
    dashPx?: [number, number]
  }
  /**
   * How `xs` was sampled, which is what decides whether a wide step is a data
   * hole or just the next bucket.
   *
   * 'grid' (sentinels): scores sit on a uniform interval with gated rows
   * dropped, so the minimum spacing IS the interval and anything wider is a
   * hole the band must break at rather than interpolate across.
   *
   * 'strided' (the experiment band): the service caps the train series at a
   * point budget and strides it, so a multi-year window comes back with its
   * history thinned far harder than its recent span. Consecutive buckets are
   * a stride apart by construction — treating those steps as holes leaves the
   * band and its center line drawn as sub-pixel slivers, i.e. invisible.
   */
  sampling?: 'grid' | 'strided'
  showBand?: boolean
  showOutOfBand?: boolean
  showResidualLane?: boolean
}

type SplitRole = 'train' | 'val' | 'test' | 'forecast'
interface SplitRegion {
  startMs: number
  endMs: number
  role: SplitRole
}

/** Faint background tint per split role (train is the lightest baseline). */
const REGION_TINT: Record<SplitRole, string> = {
  train: 'rgba(34, 211, 238, 0.05)',
  val: 'rgba(249, 115, 22, 0.08)',
  test: 'rgba(148, 163, 184, 0.11)',
  forecast: 'rgba(245, 158, 11, 0.09)',
}
const REGION_STROKE: Record<SplitRole, string> = {
  train: '#22d3ee',
  val: '#f97316',
  test: '#94a3b8',
  forecast: '#f59e0b',
}
const REGION_LABEL: Record<SplitRole, string> = {
  train: 'TRAIN',
  val: 'VAL',
  test: 'TEST',
  forecast: 'FORECAST',
}

/**
 * A sentinel's train → test → forecast spans. A design/plan overlay carries
 * them explicitly (`splitRanges`); a served band derives them from the honest
 * train→test split (`trainedSplitMs`) and the test→forecast boundary
 * (`modelCreatedMs`), anchored to the scored series so a region never bleeds
 * past where the model has data.
 */
function regionsOf(
  overlay: Pick<
    SentinelBandOverlay,
    'xs' | 'trainedSplitMs' | 'modelCreatedMs' | 'windowStartMs' | 'splitRanges'
  >
): SplitRegion[] {
  if (overlay.splitRanges?.length) {
    return overlay.splitRanges
      .filter((r) => r.endMs > r.startMs)
      .map((r) => ({
        startMs: r.startMs,
        endMs: r.endMs,
        role: (REGION_LABEL as Record<string, string>)[r.role]
          ? (r.role as SplitRole)
          : 'train',
      }))
  }
  const t = overlay.trainedSplitMs
  const m = overlay.modelCreatedMs
  // Anchor to the MODEL's window, not where the scored series ends — historian
  // data can stop before the split, and using xs would collapse test/forecast.
  const start =
    overlay.windowStartMs ?? (overlay.xs.length ? overlay.xs[0] : (t ?? m))
  if (start === null) return []
  // Forecast runs to +∞ (the draw clamps it to the visible edge), so it shows
  // whether or not the series reaches that far.
  if (t !== null && m !== null && m > t) {
    return [
      { startMs: start, endMs: t, role: 'train' },
      { startMs: t, endMs: m, role: 'test' },
      { startMs: m, endMs: Infinity, role: 'forecast' },
    ]
  }
  if (t !== null && t > start) {
    return [
      { startMs: start, endMs: t, role: 'train' },
      { startMs: t, endMs: Infinity, role: 'test' },
    ]
  }
  if (m !== null && m > start) {
    return [
      { startMs: start, endMs: m, role: 'train' },
      { startMs: m, endMs: Infinity, role: 'forecast' },
    ]
  }
  return []
}

/** Faint full-height region tints (drawn behind the pens). */
function drawRegionTints(u: uPlot, regions: SplitRegion[]): void {
  const xMin = u.scales.x.min ?? -Infinity
  const xMax = u.scales.x.max ?? Infinity
  const ctx = u.ctx
  ctx.save()
  ctx.setLineDash([])
  for (const region of regions) {
    const s = Math.max(region.startMs, xMin)
    const e = Math.min(region.endMs, xMax)
    if (!(e > s)) continue
    const x0 = u.valToPos(s, 'x', true)
    const x1 = u.valToPos(e, 'x', true)
    ctx.fillStyle = REGION_TINT[region.role]
    ctx.fillRect(x0, u.bbox.top, x1 - x0, u.bbox.height)
  }
  ctx.restore()
}

/**
 * Region labels centered at the top of each span, plus a thin dashed boundary
 * line between spans (drawn on top of the pens). This is the "TRAIN / TEST /
 * FORECAST across the top" layer the user asked for.
 */
function drawRegionLabels(u: uPlot, regions: SplitRegion[]): void {
  const xMin = u.scales.x.min ?? -Infinity
  const xMax = u.scales.x.max ?? Infinity
  const ctx = u.ctx
  regions.forEach((region, index) => {
    // Boundary line at the start of every region after the first.
    if (index > 0 && region.startMs >= xMin && region.startMs <= xMax) {
      const bx = u.valToPos(region.startMs, 'x', true)
      ctx.save()
      ctx.beginPath()
      ctx.setLineDash([6 * devicePixelRatio, 6 * devicePixelRatio])
      ctx.lineWidth = 1 * devicePixelRatio
      ctx.strokeStyle = REGION_STROKE[region.role]
      ctx.globalAlpha = 0.8
      ctx.moveTo(bx, u.bbox.top)
      ctx.lineTo(bx, u.bbox.top + u.bbox.height)
      ctx.stroke()
      ctx.restore()
    }
    // Centered label over the visible portion of the region.
    const s = Math.max(region.startMs, xMin)
    const e = Math.min(region.endMs, xMax)
    if (!(e > s)) return
    const cx = (u.valToPos(s, 'x', true) + u.valToPos(e, 'x', true)) / 2
    ctx.save()
    ctx.globalAlpha = 0.85
    ctx.fillStyle = REGION_STROKE[region.role]
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Canvas font syntax is deferred with canvas labels.
    ctx.font = `600 ${11 * devicePixelRatio}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.fillText(
      REGION_LABEL[region.role],
      cx,
      u.bbox.top + 4 * devicePixelRatio
    )
    ctx.restore()
  })
}

/**
 * Sentinel alerting ranges: a mostly-transparent fill between the upper and
 * lower band (expected ± band) with a dotted center line tracing the model's
 * expected value, drawn around each sentinel's target pen on that pen's own
 * y-scale. Edge strokes exist but are currently fully transparent — the fill
 * marks the band's extent.
 */
export function sentinelBandsPlugin(
  options: SentinelBandsPluginOptions
): uPlot.Plugin {
  const { overlays, sensorAxisMapping, singleScaleMode } = options
  const sampling = options.sampling ?? 'grid'
  const showBand = options.showBand ?? true
  const showOutOfBand = options.showOutOfBand ?? true
  const showResidualLane = options.showResidualLane ?? false
  const centerLine = {
    color: '#ffffff',
    alpha: 0.2,
    widthPx: 1.5,
    dashPx: [3, 5] as [number, number],
    ...options.centerLine,
  }
  type SentinelOverlay = SentinelBandsPluginOptions['overlays'][number]

  /**
   * The y-scale the overlay's target pen draws on, or null when the pen has
   * no scale on this chart (multi-scale mode creates one fixed-range scale
   * per mapped sensor and no 'y' scale at all). Null means SKIP the overlay:
   * the previous silent fallback to 'y' fed valToPos a nonexistent scale and
   * rendered the band at meaningless positions.
   */
  const scaleKeyFor = (targetSensorId: string): string | null =>
    singleScaleMode
      ? 'y'
      : sensorAxisMapping[targetSensorId]
        ? targetSensorId
        : null

  /**
   * Score buckets are NOT uniformly spaced — the service's cleaning gates
   * drop rows, leaving multi-hour holes in `xs`. Everything time-shaped in
   * this plugin (bucket matching, run contiguity, block widths) goes through
   * this geometry instead of assuming `xs[1] - xs[0]` is the cadence.
   */
  const bucketGeometry = (overlay: SentinelOverlay) => {
    const { xs } = overlay
    const cadence = scoreCadenceMs(xs)
    // Bucket WIDTHS stay keyed to the cadence (a bucket summarizes one
    // interval however sparsely the series was sampled); only run contiguity
    // uses the sampling-aware limit.
    const contiguityLimit =
      overlay.gapToleranceMs ??
      (sampling === 'strided'
        ? scoreContiguityLimitMs(xs)
        : cadence * SCORE_GAP_FACTOR)
    return {
      cadence,
      /** A cleaning gap sits between bucket i-1 and i: runs must break. */
      gapBefore: (i: number) => i > 0 && xs[i] - xs[i - 1] > contiguityLimit,
      /** Half-width toward the previous bucket, never across a gap. */
      halfLeft: (i: number) =>
        i > 0 ? Math.min(xs[i] - xs[i - 1], cadence) / 2 : cadence / 2,
      /** Half-width toward the next bucket, never across a gap. */
      halfRight: (i: number) =>
        i < xs.length - 1
          ? Math.min(xs[i + 1] - xs[i], cadence) / 2
          : cadence / 2,
      /** The bucket a chart sample belongs to, or null inside a gap. */
      bucketFor: (timestampMs: number): number | null => {
        const index = nearestBucketIndex(xs, timestampMs)
        if (index === null) return null
        return Math.abs(xs[index] - timestampMs) > cadence * 0.75 ? null : index
      },
    }
  }

  /**
   * The band interior as one polygon per contiguous non-null run (up along
   * the upper edge, back along the lower). Runs also break at cleaning gaps
   * so the band never interpolates across spans the model was gated out of.
   * Filled in `draw` as the shaded region and used in `drawClear` to clip
   * the glow out of the band.
   */
  const buildBandPath = (
    u: uPlot,
    overlay: SentinelOverlay,
    scaleKey: string,
    gapBefore: (i: number) => boolean
  ): Path2D => {
    const path = new Path2D()
    let runStart = -1
    const closeRun = (endExclusive: number) => {
      if (runStart < 0) return
      for (let i = runStart; i < endExclusive; i++) {
        const x = u.valToPos(overlay.xs[i], 'x', true)
        const y = u.valToPos(overlay.upper[i] as number, scaleKey, true)
        if (i === runStart) path.moveTo(x, y)
        else path.lineTo(x, y)
      }
      for (let i = endExclusive - 1; i >= runStart; i--) {
        path.lineTo(
          u.valToPos(overlay.xs[i], 'x', true),
          u.valToPos(overlay.lower[i] as number, scaleKey, true)
        )
      }
      path.closePath()
      runStart = -1
    }
    for (let i = 0; i < overlay.xs.length; i++) {
      const hasValue =
        overlay.upper[i] !== null &&
        overlay.upper[i] !== undefined &&
        overlay.lower[i] !== null &&
        overlay.lower[i] !== undefined
      if (hasValue) {
        if (runStart >= 0 && gapBefore(i)) closeRun(i)
        if (runStart < 0) runStart = i
      } else {
        closeRun(i)
      }
    }
    closeRun(overlay.xs.length)
    return path
  }

  return {
    hooks: {
      // Fires after the canvas clears but before the series paint, so
      // everything drawn here sits BEHIND the pen lines.
      drawClear: (u: uPlot) => {
        if (overlays.length === 0) return

        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        // Backmost: faint TRAIN/TEST/FORECAST region tints, but only for a
        // SINGLE model on the chart — several models' regions would overlap
        // into mud, so with more than one we fall back to color-coded boundary
        // lines (in the draw pass) instead.
        const tintOverlays = overlays.filter(
          (overlay) =>
            overlay.showSplitRegions !== false && regionsOf(overlay).length > 0
        )
        if (tintOverlays.length === 1) {
          drawRegionTints(u, regionsOf(tintOverlays[0]))
        }

        overlays.forEach((overlay) => {
          if (!showOutOfBand || overlay.xs.length === 0) return
          if (overlay.showOutOfBandMarks === false) return
          const scaleKey = scaleKeyFor(overlay.targetSensorId)
          if (scaleKey === null) return
          const outOfBandAt = (index: number) => {
            const sigma = overlay.sigma[index]
            return (
              sigma !== null &&
              sigma !== undefined &&
              Math.abs(sigma) >= (overlay.warnSigma ?? SIGMA_WARN)
            )
          }
          const { gapBefore, halfLeft, halfRight, bucketFor } =
            bucketGeometry(overlay)

          // Out-of-band glow, drawn behind the pen. A fixed-width stroke
          // along the scored mean vanishes under a noisy pen whose raw
          // spread is wider than the stroke, and a stroked path renders
          // nothing at all for a single-bucket run. So instead: build the
          // pen's raw min/max envelope per score bucket (falling back to
          // the scored actual where the pen has no samples) and fill a
          // padded blob per contiguous run — it always reaches past the
          // data's own fuzz and has area even for one bad bucket.
          const penSeriesIndex = u.series.findIndex(
            (series) =>
              (series as { _sensorId?: string })._sensorId ===
              overlay.targetSensorId
          )
          const bucketMin = new Array<number | null>(overlay.xs.length).fill(
            null
          )
          const bucketMax = new Array<number | null>(overlay.xs.length).fill(
            null
          )
          if (penSeriesIndex > 0) {
            const times = u.data[0]
            const values = u.data[penSeriesIndex]
            for (let j = 0; j < times.length; j++) {
              const value = values?.[j]
              if (value === null || value === undefined) continue
              // Nearest-actual-bucket matching: `xs` has cleaning gaps, so
              // uniform index arithmetic mis-assigns every sample after the
              // first gap. Samples inside a gap belong to no bucket at all.
              const bucket = bucketFor(times[j])
              if (bucket === null) continue
              if (!outOfBandAt(bucket)) continue
              const currentMin = bucketMin[bucket]
              const currentMax = bucketMax[bucket]
              if (currentMin === null || value < currentMin)
                bucketMin[bucket] = value
              if (currentMax === null || value > currentMax)
                bucketMax[bucket] = value
            }
          }

          // The 2σ band takes priority over the glow: clip to the plot area
          // MINUS the band polygon (even-odd), so the glow never overlaps
          // the model's shaded region.
          ctx.save()
          const glowClip = new Path2D()
          glowClip.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
          if (showBand) {
            glowClip.addPath(buildBandPath(u, overlay, scaleKey, gapBefore))
          }
          ctx.clip(glowClip, 'evenodd')

          ctx.setLineDash([])
          ctx.fillStyle = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowColor = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowBlur = 8 * devicePixelRatio
          ctx.globalAlpha = 0.85
          const pad = 4 * devicePixelRatio

          let runStart = -1
          const fillGlowRun = (endExclusive: number) => {
            if (runStart < 0) return
            ctx.beginPath()
            for (let i = runStart; i < endExclusive; i++) {
              const top = bucketMax[i] ?? overlay.actual[i]
              if (top === null || top === undefined) continue
              const x =
                i === runStart
                  ? u.valToPos(overlay.xs[i] - halfLeft(i), 'x', true)
                  : u.valToPos(overlay.xs[i], 'x', true)
              const y = u.valToPos(top, scaleKey, true) - pad
              if (i === runStart) ctx.moveTo(x, y)
              else ctx.lineTo(x, y)
            }
            for (let i = endExclusive - 1; i >= runStart; i--) {
              const bottom = bucketMin[i] ?? overlay.actual[i]
              if (bottom === null || bottom === undefined) continue
              const x =
                i === endExclusive - 1
                  ? u.valToPos(overlay.xs[i] + halfRight(i), 'x', true)
                  : u.valToPos(overlay.xs[i], 'x', true)
              ctx.lineTo(x, u.valToPos(bottom, scaleKey, true) + pad)
            }
            ctx.closePath()
            ctx.fill()
            runStart = -1
          }
          for (let i = 0; i < overlay.xs.length; i++) {
            const hasAnchor =
              (bucketMax[i] ?? overlay.actual[i]) !== null &&
              (bucketMax[i] ?? overlay.actual[i]) !== undefined
            if (outOfBandAt(i) && hasAnchor) {
              // A cleaning gap severs the run: two excursions separated by a
              // gated-out span are separate blobs, not one smear across it.
              if (runStart >= 0 && gapBefore(i)) fillGlowRun(i)
              if (runStart < 0) runStart = i
            } else {
              fillGlowRun(i)
            }
          }
          fillGlowRun(overlay.xs.length)

          ctx.restore()
        })

        ctx.restore()
      },
      draw: (u: uPlot) => {
        if (overlays.length === 0) return

        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        overlays.forEach((overlay) => {
          if (overlay.xs.length === 0) return
          const scaleKey = scaleKeyFor(overlay.targetSensorId)
          if (scaleKey === null) return
          const { gapBefore, halfLeft, halfRight } = bucketGeometry(overlay)

          if (showBand) {
            ctx.globalAlpha = 0.2
            ctx.fillStyle = overlay.color
            ctx.fill(buildBandPath(u, overlay, scaleKey, gapBefore))
            ctx.globalAlpha = 1
            const strokeSeries = (values: (number | null)[]) => {
              let penDown = false
              for (let i = 0; i < overlay.xs.length; i++) {
                const value = values[i]
                if (value === null || value === undefined) {
                  penDown = false
                  continue
                }
                if (gapBefore(i)) penDown = false
                const x = u.valToPos(overlay.xs[i], 'x', true)
                const y = u.valToPos(value, scaleKey, true)
                if (penDown) {
                  ctx.lineTo(x, y)
                } else {
                  ctx.moveTo(x, y)
                  penDown = true
                }
              }
              ctx.stroke()
              ctx.globalAlpha = 1
            }

            ;[overlay.upper, overlay.lower].forEach((edge) => {
              ctx.beginPath()
              ctx.setLineDash([])
              ctx.lineWidth = 1.5 * devicePixelRatio
              ctx.strokeStyle = overlay.color
              ctx.globalAlpha = 0.0
              strokeSeries(edge)
            })

            // Yield the center to the pen that already draws it, when there is
            // one: two lines for one expectation, under two different names, is
            // the confusion this flag exists to prevent.
            if (!overlay.centerPenId) {
              ctx.beginPath()
              ctx.setLineDash([
                centerLine.dashPx[0] * devicePixelRatio,
                centerLine.dashPx[1] * devicePixelRatio,
              ])
              ctx.lineWidth = centerLine.widthPx * devicePixelRatio
              ctx.strokeStyle = centerLine.color
              ctx.globalAlpha = centerLine.alpha
              strokeSeries(overlay.center)
            }
          }

          if (showResidualLane) {
            const laneH = 56 * devicePixelRatio
            const laneTop = u.bbox.top + u.bbox.height - laneH
            const laneMid = laneTop + laneH / 2
            const padY = 4 * devicePixelRatio
            const residAt = (index: number): number | null => {
              const actual = overlay.actual[index]
              const center = overlay.center[index]
              return typeof actual === 'number' && typeof center === 'number'
                ? actual - center
                : null
            }
            const bandHalfAt = (index: number): number | null => {
              const upper = overlay.upper[index]
              const center = overlay.center[index]
              return typeof upper === 'number' && typeof center === 'number'
                ? upper - center
                : null
            }
            let laneScale = 0
            for (let i = 0; i < overlay.xs.length; i++) {
              const resid = residAt(i)
              const half = bandHalfAt(i)
              if (resid !== null)
                laneScale = Math.max(laneScale, Math.abs(resid))
              if (half !== null) laneScale = Math.max(laneScale, half)
            }
            if (laneScale > 0) {
              const yFor = (value: number) =>
                laneMid - (value / laneScale) * (laneH / 2 - padY)
              const strokeLane = (
                valueAt: (index: number) => number | null
              ) => {
                let penDown = false
                ctx.beginPath()
                for (let i = 0; i < overlay.xs.length; i++) {
                  const value = valueAt(i)
                  if (value === null) {
                    penDown = false
                    continue
                  }
                  if (gapBefore(i)) penDown = false
                  const x = u.valToPos(overlay.xs[i], 'x', true)
                  const y = yFor(value)
                  if (penDown) {
                    ctx.lineTo(x, y)
                  } else {
                    ctx.moveTo(x, y)
                    penDown = true
                  }
                }
                ctx.stroke()
              }

              ctx.setLineDash([])
              ctx.globalAlpha = 0.62
              ctx.fillStyle = 'rgba(9, 9, 11, 0.9)'
              ctx.fillRect(u.bbox.left, laneTop, u.bbox.width, laneH)
              ctx.globalAlpha = 0.4
              ctx.strokeStyle = '#3f3f46'
              ctx.lineWidth = 1 * devicePixelRatio
              ctx.beginPath()
              ctx.moveTo(u.bbox.left, laneTop)
              ctx.lineTo(u.bbox.left + u.bbox.width, laneTop)
              ctx.stroke()
              ctx.globalAlpha = 0.35
              ctx.beginPath()
              ctx.moveTo(u.bbox.left, laneMid)
              ctx.lineTo(u.bbox.left + u.bbox.width, laneMid)
              ctx.stroke()

              ctx.setLineDash([3 * devicePixelRatio, 3 * devicePixelRatio])
              ctx.lineWidth = 1 * devicePixelRatio
              ctx.strokeStyle = '#fb923c'
              ctx.globalAlpha = 0.55
              strokeLane(bandHalfAt)
              strokeLane((index) => {
                const half = bandHalfAt(index)
                return half === null ? null : -half
              })

              ctx.setLineDash([])
              ctx.lineWidth = 1.25 * devicePixelRatio
              ctx.strokeStyle = overlay.color
              ctx.globalAlpha = 0.95
              strokeLane(residAt)

              ctx.globalAlpha = 0.75
              ctx.fillStyle = '#a1a1aa'
              // eslint-disable-next-line lingui/no-unlocalized-strings -- Canvas font syntax is deferred with canvas labels.
              ctx.font = `${9 * devicePixelRatio}px Manrope, sans-serif`
              ctx.fillText(
                'RESIDUAL',
                u.bbox.left + 6 * devicePixelRatio,
                laneTop + 12 * devicePixelRatio
              )
              ctx.globalAlpha = 1
            }
          }

          if (!showOutOfBand) return

          // Out-of-bound treatment (all in the one sentinel blue; the glow
          // behind the pen is drawn in drawClear): faint full-height columns
          // locate excursion episodes, and a thin lane along the chart
          // bottom is the excursion timeline.
          if (overlay.showOutOfBandMarks === false) return
          const outOfBandAt = (index: number) => {
            const sigma = overlay.sigma[index]
            return (
              sigma !== null &&
              sigma !== undefined &&
              Math.abs(sigma) >= (overlay.warnSigma ?? SIGMA_WARN)
            )
          }

          // Episode columns: one low-alpha slab per contiguous run.
          ctx.setLineDash([])
          ctx.globalAlpha = 0.07
          ctx.fillStyle = SENTINEL_OUT_OF_BAND_COLOR
          let columnStart = -1
          const flushColumn = (endExclusive: number) => {
            if (columnStart < 0) return
            const x0 = u.valToPos(
              overlay.xs[columnStart] - halfLeft(columnStart),
              'x',
              true
            )
            const x1 = u.valToPos(
              overlay.xs[endExclusive - 1] + halfRight(endExclusive - 1),
              'x',
              true
            )
            ctx.fillRect(x0, u.bbox.top, x1 - x0, u.bbox.height)
            columnStart = -1
          }
          for (let i = 0; i < overlay.xs.length; i++) {
            if (outOfBandAt(i)) {
              // Episodes on either side of a cleaning gap are separate
              // columns; a single slab would paint the gap as an excursion.
              if (columnStart >= 0 && gapBefore(i)) flushColumn(i)
              if (columnStart < 0) columnStart = i
            } else {
              flushColumn(i)
            }
          }
          flushColumn(overlay.xs.length)

          // Excursion lane pinned to the chart bottom: full-opacity blocks
          // with a minimum on-screen width so single bad buckets survive
          // wide zooms, glowing slightly to separate from the background.
          const laneHeight = 2 * devicePixelRatio
          const laneY = u.bbox.top + u.bbox.height - laneHeight
          const minBlockWidth = 3 * devicePixelRatio
          ctx.globalAlpha = 1
          ctx.fillStyle = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowColor = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowBlur = 2 * devicePixelRatio
          for (let i = 0; i < overlay.xs.length; i++) {
            if (!outOfBandAt(i)) continue
            const x0 = u.valToPos(overlay.xs[i] - halfLeft(i), 'x', true)
            const x1 = u.valToPos(overlay.xs[i] + halfRight(i), 'x', true)
            const width = Math.max(x1 - x0, minBlockWidth)
            const center = (x0 + x1) / 2
            ctx.fillRect(center - width / 2, laneY, width, laneHeight)
          }
          ctx.shadowBlur = 0
          ctx.globalAlpha = 1
        })

        // On top of the band: the TRAIN / TEST / FORECAST layer. A single model
        // gets centered region labels + dashed boundary lines (paired with the
        // tints drawn behind); several models get just color-coded boundary
        // lines so their splits stay distinguishable without a wall of labels.
        const xMin = u.scales.x.min ?? -Infinity
        const xMax = u.scales.x.max ?? Infinity
        const labelOverlays = overlays.filter(
          (overlay) =>
            overlay.showSplitRegions !== false && regionsOf(overlay).length > 0
        )
        if (labelOverlays.length === 1) {
          drawRegionLabels(u, regionsOf(labelOverlays[0]))
        } else {
          const drawn = new Set<number>()
          const line = (ms: number | null, color: string) => {
            if (ms === null || ms < xMin || ms > xMax) return
            const key = Math.round(ms / 60000)
            if (drawn.has(key)) return
            drawn.add(key)
            const x = u.valToPos(ms, 'x', true)
            ctx.save()
            ctx.beginPath()
            ctx.setLineDash([6 * devicePixelRatio, 6 * devicePixelRatio])
            ctx.lineWidth = 1 * devicePixelRatio
            ctx.strokeStyle = color
            ctx.globalAlpha = 0.7
            ctx.moveTo(x, u.bbox.top)
            ctx.lineTo(x, u.bbox.top + u.bbox.height)
            ctx.stroke()
            ctx.restore()
          }
          labelOverlays.forEach((overlay) => {
            line(overlay.trainedSplitMs, overlay.color)
            line(overlay.modelCreatedMs, overlay.color)
          })
        }

        ctx.restore()
      },
    },
  }
}

interface SentinelCasesPluginOptions {
  cases: SentinelCaseMarker[]
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  singleScaleMode: boolean
  /** Clicking a bubble opens that case in the Sentinels panel. */
  onCaseClick?: (sentinelEntryId: string, caseId: string) => void
}

/** Click radius (CSS px) for hit-testing a case bubble. */
const CASE_HIT_RADIUS_PX = 18

/** Bubble colors by case state. */
const CASE_OPEN_COLOR = '#60a5fa'
const CASE_DIAGNOSED_COLOR = '#34d399'
const CASE_BOOKMARK_COLOR = '#94a3b8'

/**
 * Cases as bubbles on the chart, at each breach's (time, actual) on its
 * sentinel's target pen — so "where a case came from" is visible in place
 * rather than only in a side list. Open breaches read blue, diagnosed ones
 * green, and a manually-bookmarked in-band point is a hollow ring (not an
 * alarm). Drawn on top of the pens; skips a case whose target pen has no scale
 * on this chart (multi-scale mode), matching the band plugin's guard.
 */
export function sentinelCasesPlugin(
  options: SentinelCasesPluginOptions
): uPlot.Plugin {
  const { cases, sensorAxisMapping, singleScaleMode, onCaseClick } = options
  const scaleKeyFor = (targetSensorId: string): string | null =>
    singleScaleMode
      ? 'y'
      : sensorAxisMapping[targetSensorId]
        ? targetSensorId
        : null

  let tooltip: HTMLDivElement | null = null
  const hideTooltip = () => {
    if (tooltip) tooltip.style.display = 'none'
  }
  const showTooltip = (u: uPlot, marker: SentinelCaseMarker) => {
    if (!tooltip) {
      tooltip = document.createElement('div')
      /* eslint-disable lingui/no-unlocalized-strings -- CSS declaration syntax, not user-facing copy. */
      tooltip.style.cssText =
        'position:absolute;pointer-events:none;z-index:30;display:none;' +
        'background:rgba(13,18,23,0.94);color:#e6eaec;border:1px solid rgba(255,255,255,0.16);' +
        'border-radius:6px;padding:6px 8px;font:11px/1.45 -apple-system,system-ui,sans-serif;' +
        'max-width:15rem;box-shadow:0 2px 10px rgba(0,0,0,0.35)'
      /* eslint-enable lingui/no-unlocalized-strings */
      u.over.appendChild(tooltip)
    }
    const scaleKey = scaleKeyFor(marker.targetSensorId)
    if (scaleKey === null) return
    const when = new Date(marker.breachMs).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    const magnitude =
      marker.magnitude !== undefined && marker.magnitude !== null
        ? t` · ${{ magnitude: marker.magnitude.toFixed(1) }}× band`
        : ''
    const state = marker.diagnosed
      ? t`Diagnosed`
      : marker.inBand
        ? t`Bookmarked point`
        : marker.caseId
          ? t`Open case — click to view`
          : t`No case yet`
    tooltip.textContent = ''
    const titleLine = document.createElement('div')
    titleLine.style.fontWeight = '600'
    titleLine.textContent = marker.title || t`Detection`
    const metaLine = document.createElement('div')
    metaLine.style.opacity = '0.8'
    metaLine.textContent = `${when}${magnitude} · ${state}`
    tooltip.append(titleLine, metaLine)
    const x = u.valToPos(marker.breachMs, 'x')
    const y = u.valToPos(marker.value, scaleKey)
    tooltip.style.display = 'block'
    const width = tooltip.offsetWidth
    const overWidth = u.over.clientWidth
    tooltip.style.left = `${Math.max(0, Math.min(x + 12, overWidth - width - 4))}px`
    tooltip.style.top = `${Math.max(0, y - tooltip.offsetHeight - 14)}px`
  }

  return {
    hooks: {
      destroy: () => {
        tooltip?.remove()
        tooltip = null
      },
      ready: (u: uPlot) => {
        // Nearest bubble to a pointer position, in the SAME frame the bubbles
        // are drawn in: canvas device pixels (valToPos(..., true)). Measuring
        // against the canvas — not u.over — keeps the y-axis gutter from
        // offsetting x.
        const hitTest = (clientX: number, clientY: number) => {
          const canvas = u.ctx.canvas
          const rect = canvas.getBoundingClientRect()
          const scaleX = rect.width ? canvas.width / rect.width : 1
          const scaleY = rect.height ? canvas.height / rect.height : 1
          const cx = (clientX - rect.left) * scaleX
          const cy = (clientY - rect.top) * scaleY
          const hitR = CASE_HIT_RADIUS_PX * scaleX
          let best: SentinelCaseMarker | null = null
          let bestDist = Infinity
          for (const kase of cases) {
            const scaleKey = scaleKeyFor(kase.targetSensorId)
            if (scaleKey === null) continue
            const x = u.valToPos(kase.breachMs, 'x', true)
            const y = u.valToPos(kase.value, scaleKey, true)
            const dist = Math.hypot(x - cx, y - cy)
            if (dist < bestDist) {
              bestDist = dist
              best = kase
            }
          }
          return best && bestDist <= hitR ? best : null
        }

        // A press that STARTS on a bubble is a case click, not a zoom: swallow
        // the mousedown so uPlot never begins its box-zoom drag there, then open
        // the case on release (as long as it wasn't dragged far). A press that
        // starts anywhere else is left entirely to uPlot's zoom/pan.
        let pressed: SentinelCaseMarker | null = null
        let downX = 0
        let downY = 0
        u.over.addEventListener(
          'mousedown',
          (event: MouseEvent) => {
            pressed = hitTest(event.clientX, event.clientY)
            if (pressed) {
              downX = event.clientX
              downY = event.clientY
              event.stopPropagation()
              event.preventDefault()
            }
          },
          true
        )
        u.over.addEventListener(
          'mouseup',
          (event: MouseEvent) => {
            const target = pressed
            pressed = null
            if (!target) return
            event.stopPropagation()
            const moved = Math.hypot(
              event.clientX - downX,
              event.clientY - downY
            )
            // A tiny drift still counts as a click; a real drag off the bubble
            // is treated as "changed my mind" and does nothing. An alert
            // marker with no case yet has nowhere to go — swallowed, not
            // routed.
            if (moved <= CASE_HIT_RADIUS_PX && target.caseId && onCaseClick) {
              onCaseClick(target.sentinelEntryId, target.caseId)
            }
          },
          true
        )
        u.over.addEventListener('mousemove', (event: MouseEvent) => {
          const hit = hitTest(event.clientX, event.clientY)
          u.over.style.cursor = hit && hit.caseId ? 'pointer' : ''
          if (hit) {
            showTooltip(u, hit)
          } else {
            hideTooltip()
          }
        })
        u.over.addEventListener('mouseleave', () => {
          u.over.style.cursor = ''
          hideTooltip()
        })
      },
      draw: (u: uPlot) => {
        if (cases.length === 0) return
        const ctx = u.ctx
        const xMin = u.scales.x.min ?? -Infinity
        const xMax = u.scales.x.max ?? Infinity
        const r = 9 * devicePixelRatio
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()
        for (const kase of cases) {
          const scaleKey = scaleKeyFor(kase.targetSensorId)
          if (scaleKey === null) continue
          if (kase.breachMs < xMin || kase.breachMs > xMax) continue
          const x = u.valToPos(kase.breachMs, 'x', true)
          const y = u.valToPos(kase.value, scaleKey, true)
          if (y < u.bbox.top || y > u.bbox.top + u.bbox.height) continue
          const color = kase.diagnosed
            ? CASE_DIAGNOSED_COLOR
            : kase.inBand
              ? CASE_BOOKMARK_COLOR
              : CASE_OPEN_COLOR
          ctx.beginPath()
          ctx.arc(x, y, r, 0, Math.PI * 2)
          if (kase.inBand && !kase.diagnosed) {
            // A forced in-band bookmark is hollow — it is not a breach.
            ctx.lineWidth = 2.5 * devicePixelRatio
            ctx.strokeStyle = 'rgba(255,255,255,0.9)'
            ctx.stroke()
            ctx.lineWidth = 2 * devicePixelRatio
            ctx.strokeStyle = color
            ctx.stroke()
          } else {
            ctx.shadowColor = color
            ctx.shadowBlur = 7 * devicePixelRatio
            ctx.fillStyle = color
            ctx.globalAlpha = kase.caseId ? 0.95 : 0.8
            ctx.fill()
            ctx.shadowBlur = 0
            ctx.globalAlpha = 1
            ctx.lineWidth = 2.5 * devicePixelRatio
            ctx.strokeStyle = 'rgba(255,255,255,0.9)'
            ctx.stroke()
          }
        }
        ctx.restore()
      },
    },
  }
}

interface ExcludedDataPluginOptions {
  /**
   * Per-pen points a filter or the cleaning plan dropped. Several entries may
   * name the same pen — the lab's own removals and a pushed filter both report
   * a gated tag — and they share that pen's one lane.
   */
  excluded: Array<{
    sensorId: string
    /** Epoch-ms x positions, ascending. */
    xs: number[]
  }>
  /** Pen colors by sensor id, so a lane reads as belonging to its pen. */
  colors: Record<string, string>
}

const EXCLUDED_FALLBACK_COLOR = '#94a3b8'
const EXCLUDED_LANE_HEIGHT_PX = 2
/** Clears the sentinel excursion lane, which sits flush with the bottom. */
const EXCLUDED_LANE_BOTTOM_INSET_PX = 4
const EXCLUDED_LANE_GAP_PX = 1
const EXCLUDED_LANE_MIN_BLOCK_PX = 2

/**
 * When a filter or the cleaning plan dropped data, as a lane of blocks along
 * the chart bottom. Nothing over the trace: the pens draw the drops as breaks
 * in the line (see `excludedGapRefiner`), and this says which pen and when.
 */
export function excludedDataPlugin(
  options: ExcludedDataPluginOptions
): uPlot.Plugin {
  const { excluded, colors } = options

  const lanes = new Map<string, number[]>()
  excluded.forEach((series) => {
    const xs = lanes.get(series.sensorId)
    if (xs) xs.push(...series.xs)
    else lanes.set(series.sensorId, [...series.xs])
  })
  lanes.forEach((xs) => xs.sort((left, right) => left - right))

  return {
    hooks: {
      draw: (u: uPlot) => {
        if (lanes.size === 0) return

        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        const [scaleMin, scaleMax] = [u.scales.x.min, u.scales.x.max]
        const laneHeight = EXCLUDED_LANE_HEIGHT_PX * devicePixelRatio
        const laneStride =
          (EXCLUDED_LANE_HEIGHT_PX + EXCLUDED_LANE_GAP_PX) * devicePixelRatio
        const laneBottom =
          u.bbox.top +
          u.bbox.height -
          EXCLUDED_LANE_BOTTOM_INSET_PX * devicePixelRatio
        const minBlock = EXCLUDED_LANE_MIN_BLOCK_PX * devicePixelRatio

        let laneIndex = -1
        lanes.forEach((xs, sensorId) => {
          if (xs.length === 0) return
          laneIndex += 1

          ctx.fillStyle = colors[sensorId] ?? EXCLUDED_FALLBACK_COLOR
          ctx.globalAlpha = 0.7
          const laneY = laneBottom - laneHeight - laneIndex * laneStride
          if (laneY < u.bbox.top) return

          // Consecutive drops merge into one block: a gated stretch should read
          // as one excluded window, not as hundreds of ticks.
          let runStart: number | null = null
          let runEnd = 0
          const flush = () => {
            if (runStart === null) return
            const x0 = u.valToPos(runStart, 'x', true)
            const x1 = u.valToPos(runEnd, 'x', true)
            const width = Math.max(x1 - x0, minBlock)
            ctx.fillRect(x0, laneY, width, laneHeight)
            runStart = null
          }

          for (let i = 0; i < xs.length; i++) {
            const x = xs[i]
            if (
              (scaleMin !== undefined && x < scaleMin) ||
              (scaleMax !== undefined && x > scaleMax)
            ) {
              continue
            }
            if (runStart === null) {
              runStart = x
              runEnd = x
              continue
            }
            // A block only grows while the next drop is adjacent on screen;
            // anything further apart starts its own block.
            if (
              u.valToPos(x, 'x', true) - u.valToPos(runEnd, 'x', true) <=
              minBlock
            ) {
              runEnd = x
              continue
            }
            flush()
            runStart = x
            runEnd = x
          }
          flush()
          ctx.globalAlpha = 1
        })

        ctx.restore()
      },
    },
  }
}

interface ExperimentMarkersPluginOptions {
  /** Pen the experiment band targets — the dots draw on its y-scale. */
  targetSensorId: string
  /** Epoch ms where the test split begins, or null for no line. */
  testBoundaryMs: number | null
  /** Out-of-band buckets: parallel epoch-ms xs / actual values. */
  outOfBandXs: number[]
  outOfBandValues: number[]
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  singleScaleMode: boolean
}

/** Cap on drawn out-of-band dots; the series decimates above this. */
export const EXPERIMENT_OUT_OF_BAND_MAX_DOTS = 1500

/**
 * Experiment train-result markers, drawn AFTER the pens (draw hook) so they
 * read on top:
 * - a subtle dashed vertical line + "test" label where the test split
 *   begins, so it's obvious which part of the band is held-out scoring;
 * - a high-contrast white dot on each bucket whose actual value left
 *   expected ± band (decimated evenly above the cap so a pathological run
 *   can't paint thousands of dots).
 */
export function experimentMarkersPlugin(
  options: ExperimentMarkersPluginOptions
): uPlot.Plugin {
  const {
    targetSensorId,
    testBoundaryMs,
    outOfBandXs,
    outOfBandValues,
    sensorAxisMapping,
    singleScaleMode,
  } = options

  const scaleKeyFor = (sensorId: string): string | null =>
    singleScaleMode ? 'y' : sensorAxisMapping[sensorId] ? sensorId : null

  return {
    hooks: {
      draw: (u: uPlot) => {
        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        // Test-split demarcation line.
        const xMin = u.scales.x.min ?? -Infinity
        const xMax = u.scales.x.max ?? Infinity
        if (
          testBoundaryMs !== null &&
          testBoundaryMs >= xMin &&
          testBoundaryMs <= xMax
        ) {
          const x = u.valToPos(testBoundaryMs, 'x', true)
          ctx.beginPath()
          ctx.setLineDash([6 * devicePixelRatio, 6 * devicePixelRatio])
          ctx.lineWidth = 1 * devicePixelRatio
          ctx.strokeStyle = '#94a3b8'
          ctx.globalAlpha = 0.7
          ctx.moveTo(x, u.bbox.top)
          ctx.lineTo(x, u.bbox.top + u.bbox.height)
          ctx.stroke()
          ctx.setLineDash([])
          ctx.globalAlpha = 0.9
          ctx.fillStyle = '#94a3b8'
          // eslint-disable-next-line lingui/no-unlocalized-strings -- Canvas font syntax is deferred with canvas labels.
          ctx.font = `${10 * devicePixelRatio}px sans-serif`
          ctx.textAlign = 'left'
          ctx.textBaseline = 'top'

          /* eslint-disable lingui/no-unlocalized-strings -- Canvas labels are deferred in this extraction wave. */
          ctx.fillText(
            'test →',
            x + 4 * devicePixelRatio,
            u.bbox.top + 4 * devicePixelRatio
          )
          /* eslint-enable lingui/no-unlocalized-strings */
          ctx.globalAlpha = 1
        }

        // Out-of-band dots on the target pen's scale.
        const scaleKey = scaleKeyFor(targetSensorId)
        if (scaleKey !== null && outOfBandXs.length > 0) {
          const step = Math.max(
            1,
            Math.ceil(outOfBandXs.length / EXPERIMENT_OUT_OF_BAND_MAX_DOTS)
          )
          const radius = 2.5 * devicePixelRatio
          ctx.lineWidth = 1 * devicePixelRatio
          ctx.fillStyle = '#ffffff'
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)'
          for (let i = 0; i < outOfBandXs.length; i += step) {
            const xVal = outOfBandXs[i]
            if (xVal < xMin || xVal > xMax) continue
            const x = u.valToPos(xVal, 'x', true)
            const y = u.valToPos(outOfBandValues[i], scaleKey, true)
            ctx.beginPath()
            ctx.arc(x, y, radius, 0, Math.PI * 2)
            ctx.fill()
            ctx.stroke()
          }
        }

        ctx.restore()
      },
    },
  }
}

interface TooltipPluginOptions {
  /** Mutable ref whose `.current` holds the latest cursor-move handler. Using
   *  a ref allows the plugin identity to remain stable across re-renders so
   *  that the plugins array (and therefore uPlotOptions) doesn't change when
   *  only the callback changes. */
  onCursorMoveRef: {
    current: ((idx: number | null, u: uPlot) => void) | undefined
  }
  onCursorLeaveRef: { current: (() => void) | undefined }
}

export function tooltipPlugin(options: TooltipPluginOptions): uPlot.Plugin {
  const { onCursorMoveRef, onCursorLeaveRef } = options

  let prevIdx: number | null | undefined = null
  let isDragging = false

  return {
    hooks: {
      ready: (u: uPlot) => {
        const over = u.over

        over.addEventListener('mousedown', () => {
          isDragging = false
        })
        over.addEventListener('mousemove', (e: MouseEvent) => {
          if (e.buttons === 1) {
            isDragging = true
          }
        })
        over.addEventListener('mouseup', () => {
          isDragging = false
        })
      },
      setCursor: (u: uPlot) => {
        // Suppress tooltip updates during drag-to-zoom to avoid React state
        // updates that could trigger re-renders and chart recreation.
        if (isDragging) return

        const idx = u.cursor.idx

        if (idx !== prevIdx) {
          prevIdx = idx
          if (idx !== null && idx !== undefined) {
            onCursorMoveRef.current?.(idx, u)
          } else {
            onCursorLeaveRef.current?.()
          }
        }
      },
    },
  }
}

export function exportPlugin(): uPlot.Plugin {
  return {
    hooks: {
      ready: (u: uPlot) => {
        const button = document.createElement('button')
        button.textContent = 'PNG'
        // eslint-disable-next-line lingui/no-unlocalized-strings -- CSS declaration syntax, not user-facing copy.
        button.style.cssText = `
          position: absolute;
          top: 4px;
          right: 4px;
          z-index: 100;
          padding: 2px 8px;
          background: ${CHART_COLORS.LEGEND_BACKGROUND};
          color: ${CHART_COLORS.TEXT};
          border: 1px solid ${CHART_COLORS.LEGEND_BORDER};
          border-radius: 3px;
          cursor: pointer;
          font: ${UPLOT_TICK_FONT};
          opacity: 0;
          transition: opacity 0.2s;
        `

        u.root.style.position = 'relative'
        u.root.appendChild(button)

        u.root.addEventListener('mouseenter', () => {
          button.style.opacity = '1'
        })
        u.root.addEventListener('mouseleave', () => {
          button.style.opacity = '0'
        })

        button.addEventListener('click', () => {
          const canvas = u.ctx.canvas

          const link = document.createElement('a')
          link.download = 'time-series-chart.png'
          link.href = canvas.toDataURL('image/png')
          link.click()
        })
      },
    },
  }
}
